import { LitElement, html } from "lit";
import { customElement, state, query, property } from "lit/decorators.js";
import { provide } from "@lit/context";
import { APP_CONFIG } from "../config/attributes";
import { themeStyles } from "../styles/themes";
import { appStyles } from "../styles/app.styles";
import { themeContext, configContext, lockedContext } from "../contexts";
import { PersistenceController } from "../controllers/persistence-controller";
import { ToastController } from "../controllers/toast-controller";
import { DatasetController } from "../controllers/dataset-controller";
import { UndoController } from "../controllers/undo-controller";
import { upgradeStateToVersion } from "../utils/serialization";
import { validateColorDistinctness } from "../utils/color";
import { checkHealth, submitRatings, ApiError, getRetryAfterSeconds } from "../utils/api";
import type { Theme, RatingExport } from "../models";
import type { Demographics } from "../../shared/config.schema";

import "./bab-attribute-rater";
import "./bab-spider-chart";
import "./bab-toast-manager";
import "./bab-import-modal";
import "./bab-privacy-modal";
import "./bab-app-header";
import "./bab-subject-tabs";
import "./bab-readonly-banner";
import "./bab-name-editor";
import "./bab-action-menu";
import "./bab-rating-submission";
import "./bab-comparison-stats";

/**
 * Top-level application shell.
 */
@customElement("bab-chart-app")
export class BabChartApp extends LitElement {
  static styles = [themeStyles, appStyles];

  @query("bab-spider-chart")
  private _chartComp!: import("./bab-spider-chart").BabSpiderChart;

  @property({ type: Object })
  @provide({ context: configContext })
  config = APP_CONFIG;

  @state()
  @provide({ context: themeContext })
  theme: Theme = "system";

  @state()
  @provide({ context: lockedContext })
  locked = false;

  @state() serverOnline = false;
  @state() _activeSetIndex = 0;
  @state() private _clearPending = false;
  @state() private _showImportModal = false;
  @state() private _showPrivacyModal = false;
  @state() private _showSubmissionModal = false;
  @state() private _submitting = false;
  @state() private _chartExpanded = false;
  
  private _persistence: PersistenceController;
  private _toasts = new ToastController(this);
  private _undo: UndoController;
  private _datasets: DatasetController;
  private _clearTimer: ReturnType<typeof setTimeout> | null = null;
  private _colorChangeTimer: ReturnType<typeof setTimeout> | null = null;
  private _pendingColorChanges: Map<number, string> = new Map();
  private _initialized = false;

  private get _activeVersion(): string {
    const v = this._persistence?.state.version;
    return (v && this.config.versions[v]) ? v : this.config.currentVersion;
  }

  private get _versionAttributes() {
    const ids = this.config.versions[this._activeVersion].attributeIds;
    const attrMap = new Map(this.config.attributes.map((a) => [a.id, a]));
    return ids.map((id) => attrMap.get(id)).filter((a) => a !== undefined);
  }

  private _groupedAttributeItems() {
    const attrs = this._versionAttributes;
    type Item = { type: "attr"; attr: (typeof attrs)[number] } | { type: "header"; label: string };
    const items: Item[] = [];
    const seenCategories = new Set<string>();
    for (const attr of attrs) {
      if (attr.category && !seenCategories.has(attr.category)) {
        seenCategories.add(attr.category);
        items.push({ type: "header", label: attr.category });
      }
      items.push({ type: "attr", attr });
    }
    return items;
  }

  constructor() {
    super();
    this._persistence = new PersistenceController(this, () => this.config);
    this._undo = new UndoController(this);
    this._datasets = new DatasetController(this, this._persistence, this._toasts, () => this._undo.push(this._persistence.state));
  }

  private _onKeyDown = (e: KeyboardEvent) => {
    const meta = e.metaKey || e.ctrlKey;
    if (!meta) return;

    const active = document.activeElement;
    const shadowActive = active?.shadowRoot?.activeElement;
    if (shadowActive instanceof HTMLInputElement || shadowActive instanceof HTMLTextAreaElement) return;

    if (e.key === "z") {
      e.preventDefault();
      if (e.shiftKey) { this._onRedo(); } else { this._onUndo(); }
    } else if (e.key === "y") {
      e.preventDefault();
      this._onRedo();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._clearTimer !== null) {
      clearTimeout(this._clearTimer);
      this._clearTimer = null;
    }
    if (this._colorChangeTimer !== null) {
      clearTimeout(this._colorChangeTimer);
      this._colorChangeTimer = null;
    }
    window.removeEventListener("keydown", this._onKeyDown);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeyDown);
    // State is loaded by hostConnected() inside super.connectedCallback().
    // Sync theme and lock only on the first connection.
    if (!this._initialized) {
      this._initialized = true;
      this.theme = this._persistence.state.theme || "system";
      const hasData = this._persistence.state.datasets.some(
        d => Object.values(d.ratings).some(r => r !== null)
      );
      this.locked = hasData;
      checkHealth().then(ok => { this.serverOnline = ok; });
    }
    this._updateThemeAttribute();
    document.title = this.config.title ?? "BAB Chart";
  }

  willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    super.willUpdate(changedProperties);

    if (this._persistence) {
      // Sync theme if persistence state changed (e.g. via hashchange).
      if (this._persistence.state.theme && this._persistence.state.theme !== this.theme) {
        this.theme = this._persistence.state.theme;
        this._updateThemeAttribute();
      }
      
      // Re-lock when the browser navigates to a new hash with data.
      if (this._persistence.externalHashChange) {
        this._persistence.externalHashChange = false;
        const hasData = this._persistence.state.datasets.some(
          d => Object.values(d.ratings).some(r => r !== null)
        );
        this.locked = hasData;
      }

      // Safety check for active index bounds
      if (this._activeSetIndex >= this._persistence.state.datasets.length) {
        this._activeSetIndex = Math.max(0, this._persistence.state.datasets.length - 1);
      }
    }
  }

  private _updateThemeAttribute() {
    this.setAttribute("data-theme", this.theme);
    document.documentElement.setAttribute("data-theme", this.theme);
  }

  private _setTheme(theme: Theme) {
    this.theme = theme;
    this._persistence.update({ ...this._persistence.state, theme });
    this._updateThemeAttribute();
  }

  private _onRatingChanged(
    e: CustomEvent<{ attributeId: string; rating: number | null }>,
  ) {
    const datasets = [...this._persistence.state.datasets];
    datasets[this._activeSetIndex] = {
      ...datasets[this._activeSetIndex],
      ratings: {
        ...datasets[this._activeSetIndex].ratings,
        [e.detail.attributeId]: e.detail.rating,
      },
    };
    this._undo.push(this._persistence.state);
    this._persistence.update({ ...this._persistence.state, datasets });
  }

  private _onNameChanged(e: CustomEvent<{ name: string }>) {
    const datasets = [...this._persistence.state.datasets];
    datasets[this._activeSetIndex] = {
      ...datasets[this._activeSetIndex],
      name: e.detail.name,
    };
    this._undo.push(this._persistence.state);
    this._persistence.update({ ...this._persistence.state, datasets });
  }

  _chartBackground(): string {
    return getComputedStyle(this).getPropertyValue("--bab-card-bg").trim() || "#ffffff";
  }

  private _onColorChanged(e: CustomEvent<{ index: number; color: string }>) {
    if (this._pendingColorChanges.size === 0) {
      this._undo.push(this._persistence.state);
    }
    this._pendingColorChanges.set(e.detail.index, e.detail.color);

    if (this._colorChangeTimer !== null) clearTimeout(this._colorChangeTimer);
    this._colorChangeTimer = setTimeout(() => {
      this._colorChangeTimer = null;

      const datasets = [...this._persistence.state.datasets];
      for (const [index, color] of this._pendingColorChanges.entries()) {
        if (datasets[index]) {
          datasets[index] = { ...datasets[index], color };
        }
      }
      this._pendingColorChanges.clear();

      const allColors = datasets.map((set, i) => set.color || this.config.datasetColors[i % this.config.datasetColors.length]);
      const colorValidation = validateColorDistinctness(allColors, 4.0);
      if (!colorValidation.valid && colorValidation.conflicts) {
        const conflicts = colorValidation.conflicts
          .map(c => {
            const idx = c.indices[1];
            return datasets[idx]?.name || `Dataset ${idx + 1}`;
          })
          .join(", ");
        this._toasts.show(
          `⚠ This color is too similar to: ${conflicts}. Please choose a different color.`,
          "warn"
        );
      } else if (!validateColorDistinctness([e.detail.color, this._chartBackground()], 4.0).valid) {
        this._toasts.show(
          `⚠ This color is too similar to the chart background. Please choose a different color.`,
          "warn"
        );
      }

      this._persistence.update({ ...this._persistence.state, datasets });
    }, 80);
  }

  private _onLockChanged(e: CustomEvent<{ locked: boolean }>) {
    const nowLocked = e.detail.locked;
    this.locked = nowLocked;
    if (!nowLocked && this._activeVersion !== this.config.currentVersion) {
      const oldVersion = this._activeVersion;
      // Upgrade all datasets to the current version when the user unlocks for editing.
      const upgraded = upgradeStateToVersion(
        this._persistence.state,
        this.config.currentVersion,
        this.config,
      );
      this._persistence.update(upgraded);
      const added = this.config.versions[this.config.currentVersion].attributeIds.filter(
        id => !this.config.versions[oldVersion]?.attributeIds.includes(id)
      );
      if (added.length) {
        this._toasts.show(
          `Upgraded to ${this.config.currentVersion}: ${added.length} new ${
            added.length === 1
              ? (this.config.attributeTerm ?? "attribute")
              : (this.config.attributeTermPlural ?? "attributes")
          } added`,
          "info",
        );
      }
    }
  }

  private _onUndo() {
    const prev = this._undo.undo(this._persistence.state);
    if (prev) this._persistence.update(prev);
  }

  private _onRedo() {
    const next = this._undo.redo(this._persistence.state);
    if (next) this._persistence.update(next);
  }

  private _copyLink() {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        this._toasts.show("Link copied to clipboard!");
      })
      .catch(() => {
        this._toasts.show("Could not copy link", "warn");
      });
  }

  private _clear() {
    if (!this._clearPending) {
      this._clearPending = true;
      this._clearTimer = setTimeout(() => {
        this._clearPending = false;
      }, 3000);
      return;
    }
    clearTimeout(this._clearTimer!);
    this._clearPending = false;

    const defaultRatings: Record<string, number | null> = {};
    for (const attr of this._versionAttributes) {
      defaultRatings[attr.id] = null;
    }

    this._undo.push(this._persistence.state);
    this._persistence.update({
      ...this._persistence.state,
      datasets: [{ ratings: defaultRatings, color: this.config.datasetColors[0] }],
      version: this.config.currentVersion,
    });
    this._activeSetIndex = 0;
    this.locked = false;
    this._toasts.show("Chart cleared", "info");
  }

  private _onSubmit = async (e: CustomEvent<{ demographics: Demographics }>) => {
    if (this._submitting) return;
    this._submitting = true;
    try {
      await submitRatings(this._getActiveRatings(), e.detail.demographics);
      this._showSubmissionModal = false;
      this._toasts.show("Ratings submitted — thank you!");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const retryAfter = getRetryAfterSeconds(err.body);
        this._toasts.show(
          retryAfter !== null
            ? `Too many submissions — try again in ${retryAfter}s`
            : "Too many submissions — please try again later",
          "warn",
        );
      } else {
        this._toasts.show("Submission failed — please try again", "warn");
      }
    } finally {
      this._submitting = false;
    }
  };

  getRatings(): RatingExport[] {
    const version = this._persistence.state.version ?? this.config.currentVersion;
    const { attributeIds } = this.config.versions[version] ?? this.config.versions[this.config.currentVersion];
    return this._persistence.state.datasets.map(dataset => ({
      name: dataset.name,
      version,
      color: dataset.color,
      ratings: Object.fromEntries(
        attributeIds.map(id => [id, dataset.ratings[id] ?? null])
      ),
    }));
  }

  /** The currently selected profile only, for submission — avoids requiring demographics for every profile. */
  private _getActiveRatings(): RatingExport[] {
    const active = this.getRatings()[this._activeSetIndex];
    return active ? [active] : [];
  }

  private _downloadImage(format: "png" | "svg" = "png") {
    const dataUrl = format === "png"
      ? this._chartComp.getImageDataUrl()
      : this._chartComp.getSvgDataUrl();
    const link = document.createElement("a");
    const name = this._persistence.state.datasets[this._activeSetIndex]?.name?.trim() || `${this.config.attributeTerm || "skill"}-chart`;
    link.download = `${name.replace(/\s+/g, "-").toLowerCase()}.${format}`;
    link.href = dataUrl;
    link.click();
    this._toasts.show(`${format.toUpperCase()} saved!`);
  }

  render() {
    const activeSet = this._persistence.state.datasets[this._activeSetIndex];
    const versionAttributes = this._versionAttributes;

    return html`
      <div class="content">
        <bab-app-header 
          .theme="${this.theme}"
          @theme-changed="${(e: CustomEvent<{theme: Theme}>) => this._setTheme(e.detail.theme)}"
        ></bab-app-header>

        <div class="banner-slot">
          <bab-readonly-banner .active="${this.locked}"></bab-readonly-banner>
        </div>

        <bab-name-editor
          .name="${activeSet?.name ?? ""}"
          .locked="${this.locked}"
          @name-changed="${this._onNameChanged}"
          @lock-changed="${this._onLockChanged}"
        >
          <div class="icon-btn-group" slot="actions">
            <button
              class="icon-btn"
              ?disabled="${this.locked || !this._undo.canUndo}"
              @click="${this._onUndo}"
              aria-label="Undo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 14 4 9 9 4"></polyline>
                <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
              </svg>
            </button>
            <button
              class="icon-btn"
              ?disabled="${this.locked || !this._undo.canRedo}"
              @click="${this._onRedo}"
              aria-label="Redo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 14 20 9 15 4"></polyline>
                <path d="M4 20v-7a4 4 0 0 1 4-4h12"></path>
              </svg>
            </button>
          </div>
          ${this.serverOnline
            ? html`
                <button
                  class="submit-btn"
                  slot="actions"
                  ?disabled="${this.locked}"
                  @click="${() => this._showSubmissionModal = true}"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                  Submit Ratings
                </button>
              `
            : ""}
          <bab-action-menu
            slot="actions"
            .locked="${this.locked}"
            .clearPending="${this._clearPending}"
            @download-png="${() => this._downloadImage("png")}"
            @download-svg="${() => this._downloadImage("svg")}"
            @copy-link="${this._copyLink}"
            @clear-requested="${this._clear}"
          ></bab-action-menu>
        </bab-name-editor>

        <bab-subject-tabs
          .datasets="${this._persistence.state.datasets}"
          .activeIndex="${this._activeSetIndex}"
          .locked="${this.locked}"
          @tab-changed="${(e: CustomEvent<{index: number}>) => this._activeSetIndex = e.detail.index}"
          @add-dataset="${() => this._datasets.addDataset()}"
          @duplicate-dataset="${(e: CustomEvent<{index: number}>) => this._datasets.duplicateDataset(e.detail.index)}"
          @remove-dataset="${(e: CustomEvent<{index: number}>) => this._datasets.removeDataset(e.detail.index)}"
          @color-changed="${this._onColorChanged}"
          @open-import="${() => this._showImportModal = true}"
        ></bab-subject-tabs>

        <div class="layout">
          <div id="raters-panel" class="raters-panel ${this.locked ? "raters-panel--locked" : ""}" role="tabpanel">
            <div class="raters-header">
              <span class="raters-title">
                ${(this.config.attributeTermPlural || "Skills").charAt(0).toUpperCase() + (this.config.attributeTermPlural || "Skills").slice(1)}
                ${activeSet?.name ? `: ${activeSet.name}` : ""}
              </span>
              <span class="completion-badge ${
                versionAttributes.filter(a => (activeSet?.ratings[a.id] ?? null) !== null).length === versionAttributes.length
                  ? "completion-badge--complete" : ""
              }">
                ${versionAttributes.filter(a => (activeSet?.ratings[a.id] ?? null) !== null).length} / ${versionAttributes.length}
              </span>
            </div>

            ${this._groupedAttributeItems().map((item) =>
              item.type === "header"
                ? html`<div class="group-header">${item.label}</div>`
                : html`
                    <bab-attribute-rater
                      .attribute="${item.attr}"
                      .rating="${activeSet?.ratings[item.attr.id] ?? null}"
                      .versionConfig="${this.config.versions[this._activeVersion]}"
                      @rating-changed="${this._onRatingChanged}"
                    ></bab-attribute-rater>
                  `,
            )}
          </div>

          <div class="chart-panel ${this._chartExpanded ? "chart-panel--expanded" : ""}">
            <bab-spider-chart
              .datasets="${this._persistence.state.datasets}"
              .activeVersion="${this._activeVersion}"
              @toggle-expand="${(e: CustomEvent<{expanded: boolean}>) => this._chartExpanded = e.detail.expanded}"
            ></bab-spider-chart>
          </div>
        </div>

        <bab-comparison-stats
          .datasets="${this._persistence.state.datasets}"
          .activeIndex="${this._activeSetIndex}"
          .attrs="${versionAttributes}"
        ></bab-comparison-stats>

        <footer class="app-footer">
          <button class="footer-link" @click="${() => this._showPrivacyModal = true}">
            Privacy Policy
          </button>
        </footer>
      </div>

      <bab-import-modal
        .open="${this._showImportModal}"
        @close="${() => this._showImportModal = false}"
        @import="${(e: CustomEvent<{url: string}>) => this._datasets.importDataset(e.detail.url)}"
      ></bab-import-modal>

      <bab-privacy-modal
        .open="${this._showPrivacyModal}"
        @close="${() => this._showPrivacyModal = false}"
      ></bab-privacy-modal>

      <bab-rating-submission
        .open="${this._showSubmissionModal}"
        .ratings="${this._getActiveRatings()}"
        .config="${this.config}"
        .description="${this.config.subtitle ?? ""}"
        .submitting="${this._submitting}"
        @close="${() => this._showSubmissionModal = false}"
        @submit="${this._onSubmit}"
      ></bab-rating-submission>

      <bab-toast-manager .toasts="${this._toasts.toasts}"></bab-toast-manager>
    `;
  }
}
